import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import * as Sentry from '@sentry/react'
import { enqueueSnackbar } from 'notistack'
import { type JSX, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Device } from '@/pages/Device/Device'
import DeviceType from '@/pages/Device/DeviceType'
import {
  calibrationCard,
  calibrationCardActions,
  warningCardHeader,
} from '@/utils/theme'

import CalibrationTestListItem from './CalibrationTestListItem'
import ErrorList from './ErrorList'

interface CalibrationTestStepProps {
  device: Device | null
  startSelfTest: () => void
  stopSelfTest: () => void
  lastJsonMessage: Record<string, unknown> | null
  onBack: () => void
  onNext: () => void
}

type Check = {
  code: string
  status: number
  label: string
  instructions: string
}

const CalibrationTestStep = ({
  device,
  startSelfTest,
  stopSelfTest,
  lastJsonMessage,
  onBack,
  onNext,
}: CalibrationTestStepProps): JSX.Element | null => {
  const { t } = useTranslation(['common', 'di', 'app'])
  const [checks, setChecks] = useState<Check[]>([])
  const [errorCodes, setErrorCodes] = useState<number[]>([])
  const [running, setRunning] = useState(false)
  const [satisfied, setSatisfied] = useState(false)

  const resetTests = () => {
    setChecks([])
    setErrorCodes([])
    setSatisfied(false)
    setRunning(false)
  }

  const onExpectedTestsReceived = useCallback(
    (expectedTests: string[]) => {
      setSatisfied(false)
      setChecks(
        expectedTests.map((expectedTest) => ({
          code: expectedTest,
          status: -1,
          label: t(`app:device.calibration.test.${expectedTest}`),
          instructions: t(
            `app:device.calibration.testStep.instructions.${expectedTest}`,
          ),
        })),
      )
    },
    [t],
  )

  const onSatisfiedTestsReceived = useCallback((satisfiedChecks: string[]) => {
    setChecks((prev) =>
      prev.map((checkItem) =>
        satisfiedChecks.includes(checkItem.code)
          ? { ...checkItem, status: 0 }
          : checkItem,
      ),
    )
  }, [])

  const wsMessageReceived = useCallback(
    (message: Record<string, unknown> | null) => {
      if (!message) return
      switch (message.type) {
        case 'expected':
          onExpectedTestsReceived(message.data as string[])
          break
        case 'satisfied':
          onSatisfiedTestsReceived(message.data as string[])
          break
        case 'erred':
          if (!Array.isArray(message.data)) return
          setErrorCodes(message.data as number[])
          break
        case 'finished':
        case 'calibrated':
          break
        default:
          Sentry.captureMessage(JSON.stringify(message))
          break
      }
    },
    [onExpectedTestsReceived, onSatisfiedTestsReceived],
  )

  const selfTestStatus = () => {
    if (running) return t('app:device.calibration.testStep.selfTestRunning')
    return satisfied
      ? t('app:device.calibration.testStep.selfTestDone')
      : t('app:device.calibration.testStep.selfTestNotStarted')
  }

  const stop = () => {
    setChecks((prev) =>
      prev.map((check) =>
        check.status === -1 ? { ...check, status: 1 } : check,
      ),
    )
    setRunning(false)
    stopSelfTest()
  }

  const start = () => {
    resetTests()
    setRunning(true)
    startSelfTest()
  }

  useEffect(() => {
    wsMessageReceived(lastJsonMessage)
  }, [lastJsonMessage, wsMessageReceived])

  useEffect(() => {
    setChecks([])
    setErrorCodes([])
    setSatisfied(false)
    setRunning(false)
  }, [device])

  useEffect(() => {
    if (checks.length > 0 && checks.every((c) => c.status === 0)) {
      setSatisfied(true)
      setRunning(false)
    }
  }, [checks])

  useEffect(() => {
    if (satisfied && errorCodes.length === 0) {
      enqueueSnackbar(t('app:device.calibration.testStep.selfTestOK'), {
        variant: 'success',
      })
    }
  }, [satisfied, errorCodes, t])

  if (!device) return null

  const headerTitle = device.label
    ? `${device.serial} - ${device.label}`
    : device.serial

  return (
    <>
      <Box sx={calibrationCard}>
        <Box sx={warningCardHeader}>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: '0 0 5px 0',
            }}
          >
            {headerTitle}
          </Typography>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: 0,
            }}
          >
            {DeviceType[device.type] ?? device.type}
          </Typography>
        </Box>

        <Box sx={{ padding: '0.9375rem 20px' }}>
          <Grid container>
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  onClick={running ? stop : start}
                  sx={{
                    backgroundColor: running ? '#f44336' : '#9c27b0',
                    '&:hover': {
                      backgroundColor: running ? '#d32f2f' : '#7b1fa2',
                    },
                  }}
                >
                  {running
                    ? t('app:device.calibration.testStep.stopSelfTest')
                    : t('app:device.calibration.testStep.startSelfTest')}
                </Button>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <List>
                  <ListItem>
                    <ListItemText
                      primary={selfTestStatus()}
                      disableTypography
                    />
                  </ListItem>
                  {checks.map(({ code, label, status, instructions }) => (
                    <CalibrationTestListItem
                      key={code}
                      label={label}
                      status={status}
                      description={instructions}
                    />
                  ))}
                </List>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <ErrorList id="deviceErrorList" errorCodes={errorCodes} />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={calibrationCardActions}>
        <Button variant="contained" onClick={onBack} disabled={running}>
          {t('common:general.back')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onNext}
          disabled={!satisfied || errorCodes.length > 0}
        >
          {t('common:general.next')}
        </Button>
      </Box>
    </>
  )
}

export default CalibrationTestStep
